import { useState, useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { getAvailableTimeSlots } from '../api/publicBookingApi';

const useTimeSlots = (slug, selectedDate, availabilityIds, totalServiceDuration) => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeZoneId, setTimeZoneId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Key off the ISO date value, not the Date object identity. Two Date
  // instances for the same calendar day must not retrigger the fetch effect.
  const formattedDate = selectedDate instanceof Date
    ? DateTime.fromJSDate(selectedDate).toISODate()
    : (selectedDate || null);

  // Stable reference for availability IDs array — keyed by contents, not
  // array identity, so callers that build a fresh array each render (e.g.
  // [selectedLocation.id]) don't retrigger the fetch effect on unrelated
  // re-renders.
  const availabilityIdsKey = useMemo(() => {
    if (!availabilityIds || !Array.isArray(availabilityIds) || availabilityIds.length === 0) {
      return null;
    }
    return [...availabilityIds].sort().join('|');
  }, [availabilityIds]);

  // Safe to depend only on the key: callers always produce a fresh array
  // literal (e.g. [selectedLocation.id]) — they never mutate in place — so
  // the live `availabilityIds` reference is fine to return when the contents
  // key is unchanged.
  const stableAvailabilityIds = useMemo(() => {
    if (!availabilityIdsKey) return null;
    return availabilityIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityIdsKey]);

  const fetchTimeSlots = useCallback(async (date, ids, duration) => {
    if (!slug || !date || !ids) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getAvailableTimeSlots(slug, date, ids, duration);
      setTimeSlots(result.timeSlots || []);
      setTimeZoneId(result.timeZoneId);
    } catch (err) {
      setError(err.message);
      setTimeSlots([]);
      setTimeZoneId(null);
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  // Reset state when dependencies change
  const resetTimeSlots = useCallback(() => {
    setTimeSlots([]);
    setTimeZoneId(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Fetch time slots when all required data is available
  useEffect(() => {
    if (formattedDate && stableAvailabilityIds) {
      fetchTimeSlots(formattedDate, stableAvailabilityIds, totalServiceDuration);
    } else {
      resetTimeSlots();
    }
  }, [formattedDate, stableAvailabilityIds, totalServiceDuration, fetchTimeSlots, resetTimeSlots]);

  // Retry function for error recovery
  const retryFetch = useCallback(() => {
    if (formattedDate && stableAvailabilityIds) {
      fetchTimeSlots(formattedDate, stableAvailabilityIds, totalServiceDuration);
    }
  }, [formattedDate, stableAvailabilityIds, totalServiceDuration, fetchTimeSlots]);

  return {
    timeSlots,
    timeZoneId,
    isLoading,
    error,
    retryFetch,
    hasData: formattedDate && stableAvailabilityIds
  };
};

export default useTimeSlots;